import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Play, RotateCw, X, RefreshCcw, AlertTriangle,
  Database, HardDrive, GitBranch, Sprout, UserPlus, Zap, Check, XCircle, Copy, ShieldCheck,
} from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ProvisionStatusBadge } from "./ProvisionBadges";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; }
}
function dur(sec) {
  if (sec == null) return "—";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

const TL = {
  PENDING: "bg-slate-500", RUNNING: "bg-amber-500", SUCCESS: "bg-emerald-500",
  READY: "bg-emerald-400", PREPARING: "bg-cyan-500", WORKSPACE: "bg-cyan-500",
  DATABASE: "bg-indigo-500", SEEDING: "bg-violet-500", ADMIN: "bg-purple-500",
  CONFIGURATION: "bg-sky-500", ENVIRONMENT: "bg-teal-500", VERIFYING: "bg-amber-500",
  FAILED: "bg-rose-500", CANCELLED: "bg-neutral-500",
};

const EXECUTION_PHASES = [
  "PREPARING", "WORKSPACE", "DATABASE", "SEEDING", "ADMIN", "CONFIGURATION", "ENVIRONMENT", "VERIFYING", "READY",
];

function ExecutionProgress({ status }) {
  const cur = String(status || "PENDING").toUpperCase();
  const idx = EXECUTION_PHASES.indexOf(cur);
  const isFailed = cur === "FAILED";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="prov-execution-progress">
      <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-3">Execution Progress</div>
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {EXECUTION_PHASES.map((ph, i) => {
          const done = idx >= 0 && i < idx;
          const active = idx === i;
          const dotCls = done ? "bg-emerald-400" : active ? `${TL[ph] || "bg-cyan-500"} animate-pulse` : "bg-white/10";
          const barCls = done ? "bg-emerald-400/60" : "bg-white/10";
          return (
            <React.Fragment key={ph}>
              <div className="flex flex-col items-center gap-1" data-testid={`prov-phase-${ph.toLowerCase()}`}>
                <div className={`w-3 h-3 rounded-full ${isFailed && active ? "bg-rose-500" : dotCls}`} />
                <div className={`text-[9px] font-bold uppercase tracking-wider ${done || active ? "text-emerald-200" : "text-white/40"}`}>
                  {ph}
                </div>
              </div>
              {i < EXECUTION_PHASES.length - 1 && <div className={`h-0.5 flex-1 rounded ${barCls}`} />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function Timeline({ logs = [] }) {
  if (!logs.length) return <div className="text-xs text-white/40 italic">Belum ada aktivitas.</div>;
  return (
    <ol className="relative border-l border-white/10 ml-3 space-y-4" data-testid="provision-timeline">
      {logs.map((log, idx) => (
        <li key={idx} className="ml-4">
          <div className={`absolute -left-[7px] w-3 h-3 rounded-full ${TL[log.status] || "bg-white/20"}`} data-testid={`prov-tl-dot-${idx}`} />
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-wider text-white/70">{log.status}</span>
            {log.phase && <span className="text-[9px] uppercase tracking-widest text-emerald-300/70 font-bold">{log.phase}</span>}
            <span className="text-[10px] text-white/40 font-mono">{fmt(log.at)}</span>
          </div>
          <div className="text-xs text-white/80 mt-0.5" data-testid={`prov-tl-msg-${idx}`}>{log.message}</div>
        </li>
      ))}
    </ol>
  );
}

function BlueprintCard({ icon: Icon, title, prepared, children, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-emerald-300" />
        <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold flex-1">{title}</div>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black uppercase tracking-widest ${prepared ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-white/40"}`}>
          {prepared ? "Ready" : "Blueprint"}
        </span>
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, mono = false, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5 last:border-0" data-testid={testid}>
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
      <div className={`text-xs text-white text-right truncate min-w-0 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function VerificationCard({ verification }) {
  if (!verification || !verification.checks) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="prov-verification">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={14} className="text-emerald-300" />
        <div className="text-[11px] uppercase tracking-widest text-white/60 font-bold flex-1">Verification</div>
        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${verification.ok ? "bg-emerald-500/20 text-emerald-200" : "bg-rose-500/20 text-rose-200"}`}
          data-testid="prov-verify-result">
          {verification.passed}/{verification.total} {verification.ok ? "PASS" : "FAIL"}
        </span>
      </div>
      <div className="space-y-1">
        {verification.checks.map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-xs" data-testid={`prov-verify-check-${c.name}`}>
            {c.ok ? <Check size={12} className="text-emerald-400" /> : <XCircle size={12} className="text-rose-400" />}
            <div className="text-white/90 uppercase text-[10px] tracking-wider font-bold min-w-[100px]">{c.name}</div>
            <div className="text-white/50 font-mono truncate">{c.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MasterAdminCard({ admin, tempPassword }) {
  if (!admin) return null;
  const copy = (val) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success("Password disalin ke clipboard.");
  };
  return (
    <div className="rounded-2xl border border-purple-500/30 bg-purple-500/5 p-4" data-testid="prov-master-admin">
      <div className="flex items-center gap-2 mb-3">
        <UserPlus size={14} className="text-purple-300" />
        <div className="text-[11px] uppercase tracking-widest text-white/70 font-bold flex-1">Master Admin</div>
        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${admin.created ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-white/60"}`}>
          {admin.created ? "CREATED" : "EXISTING"}
        </span>
      </div>
      <Row label="Name" value={admin.name} testid="prov-admin-name" />
      <Row label="Email" value={admin.email} mono testid="prov-admin-email" />
      <Row label="Role" value={admin.role} testid="prov-admin-role" />
      <Row label="Status" value={admin.status} testid="prov-admin-status" />
      {tempPassword && (
        <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="prov-temp-password-block">
          <div className="text-[10px] uppercase tracking-widest text-amber-200 font-black mb-1">Temporary Password (One-Time)</div>
          <div className="flex items-center gap-2">
            <code className="text-xs font-mono text-amber-100 bg-black/30 px-2 py-1 rounded flex-1 break-all" data-testid="prov-temp-password">{tempPassword}</code>
            <Button size="sm" variant="ghost" onClick={() => copy(tempPassword)} className="text-amber-200 hover:bg-amber-500/20" data-testid="prov-copy-temp-password">
              <Copy size={12} />
            </Button>
          </div>
          <div className="text-[10px] text-amber-200/70 mt-2">Simpan sekarang — password ini tidak akan tampil lagi setelah halaman di-refresh.</div>
        </div>
      )}
      {!tempPassword && admin.has_temp_password && (
        <div className="mt-3 text-[10px] text-white/40 italic" data-testid="prov-temp-password-consumed">
          Temporary password sudah pernah ditampilkan dan tidak akan tampil lagi.
        </div>
      )}
    </div>
  );
}

function WorkspaceCard({ ws }) {
  if (!ws) return null;
  return (
    <BlueprintCard icon={HardDrive} title="Workspace" prepared={!!ws.prepared} testid="prov-workspace">
      <Row label="Root" value={ws.root} mono />
      <Row label="Folders" value={`${(ws.folders || []).length}`} />
      <Row label="Baru dibuat" value={`${(ws.created || []).length}`} />
      <Row label="Skip existing" value={`${(ws.skipped_existing || []).length}`} />
    </BlueprintCard>
  );
}

export default function ProvisioningDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);   // one-time reveal after execute

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, ts] = await Promise.all([
        api.get(`/platform/provisioning/${id}`),
        api.get("/platform/tenants"),
      ]);
      setP(pr.data);
      setTenants(ts.data.items || []);
    } catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (verb, body = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/provisioning/${id}/${verb}`, body);
      toast.success(`Provisioning ${verb} berhasil.`);
      if (verb === "execute" && r.data?.provision?.temporary_password) {
        setTempPassword(r.data.provision.temporary_password);
      }
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusy(false); }
  };

  if (loading || !p) {
    return (
      <PlatformLayout title="Provisioning Detail">
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
      </PlatformLayout>
    );
  }

  const tenant = tenants.find((t) => t.tenant_id === p.tenant_id);
  const canStart = p.status === "PENDING";
  const canExecute = p.status === "PENDING";
  const canRetry = p.status === "FAILED";
  const canCancel = ["PENDING", "RUNNING", "PREPARING"].includes(p.status);
  const isExecuting = ["PREPARING", "WORKSPACE", "DATABASE", "SEEDING", "ADMIN", "CONFIGURATION", "ENVIRONMENT", "VERIFYING"].includes(p.status);
  const isReal = p.status === "READY" || isExecuting || p.status === "FAILED" && p.workspace;

  return (
    <PlatformLayout title={p.provision_number}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/provisioning")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="prov-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="prov-refresh-btn">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          {canExecute && (
            <Button
              onClick={() => {
                if (!window.confirm("Jalankan Real Provisioning?\n\nAksi ini akan:\n- Membuat workspace folder\n- Membuat MongoDB collection tenant\n- Seed roles/permissions/settings\n- Membuat MASTER_ADMIN\n- Auto-generate Configuration + Environment\n\nLanjutkan?")) return;
                act("execute", {});
              }}
              disabled={busy}
              className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white font-black"
              data-testid="prov-detail-execute"
            >
              <Zap size={14} className="mr-1" /> Run Provisioning
            </Button>
          )}
          {canStart && (
            <>
              <Button onClick={() => act("start", { simulate: "success" })} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="prov-detail-start">
                <Play size={14} className="mr-1" /> Blueprint (Success)
              </Button>
              <Button onClick={() => act("start", { simulate: "fail", error_message: "simulated failure" })} disabled={busy} className="bg-rose-500 hover:bg-rose-600 text-white" data-testid="prov-detail-start-fail">
                <Play size={14} className="mr-1" /> Blueprint (Fail sim)
              </Button>
            </>
          )}
          {canRetry && (
            <Button onClick={() => act("retry")} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="prov-detail-retry">
              <RotateCw size={14} className="mr-1" /> Retry
            </Button>
          )}
          {canCancel && (
            <Button onClick={() => {
              const reason = window.prompt("Alasan cancel?") || "";
              act("cancel", { reason });
            }} disabled={busy} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="prov-detail-cancel">
              <X size={14} className="mr-1" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {p.status === "FAILED" && p.error_message && (
        <div className="mb-4 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex items-start gap-3" data-testid="provision-error-message">
          <AlertTriangle size={18} className="text-rose-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-rose-200 font-black text-sm">Provisioning Gagal</div>
            <div className="text-xs text-rose-100/80 mt-1 font-mono break-words">{p.error_message}</div>
          </div>
        </div>
      )}

      {p.status === "READY" && (
        <div className="mb-4 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 flex items-start gap-3" data-testid="prov-ready-banner">
          <Check size={18} className="text-emerald-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-emerald-200 font-black text-sm">Tenant READY 🚀</div>
            <div className="text-xs text-emerald-100/80 mt-1">Semua 8 fase selesai dan verification lulus. Tenant siap digunakan.</div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="text-white font-display font-black text-lg" data-testid="prov-detail-number">{p.provision_number}</div>
        <ProvisionStatusBadge status={p.status} />
        <span className="text-xs text-white/50 font-mono">{p.environment}</span>
        <span className="text-xs text-white/50 font-mono">· {p.plan_code}</span>
        <span className="text-xs text-white/50 font-mono">· {tenant?.tenant_name || p.tenant_id.slice(0, 8)}</span>
        <span className="ml-auto text-[10px] text-white/40">Started {fmt(p.started_at)} · Finished {fmt(p.finished_at)} · Duration {dur(p.duration_seconds)}</span>
      </div>

      {(isExecuting || p.status === "READY" || (p.status === "FAILED" && p.workspace)) && (
        <div className="mb-4">
          <ExecutionProgress status={p.status} />
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {p.verification && <VerificationCard verification={p.verification} />}
          {isReal && p.admin_account && <MasterAdminCard admin={p.admin_account} tempPassword={tempPassword} />}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-3">Timeline</div>
            <Timeline logs={p.logs || []} />
          </div>
        </div>

        <div className="space-y-3">
          {isReal && <WorkspaceCard ws={p.workspace} />}
          <BlueprintCard icon={Database} title="Database" prepared={p.database?.prepared} testid="prov-database">
            {p.database ? (
              <div>
                <Row label="Name" value={p.database.database_name || "—"} mono />
                <Row label="Collections" value={`${p.database.collections?.length || 0}`} />
                <Row label="Indexes" value={`${p.database.indexes_plan?.length || 0}`} />
              </div>
            ) : <div className="text-[10px] text-white/40 italic">Belum di-execute.</div>}
          </BlueprintCard>
          <BlueprintCard icon={Sprout} title="Seed" prepared={!!p.seed?.seeded} testid="prov-seed">
            {p.seed ? (
              <div>
                <Row label="Roles" value={`${p.seed.roles?.length || 0}`} />
                <Row label="Permissions" value={`${p.seed.permissions?.length || 0}`} />
                {p.seed.roles_seeded_new !== undefined && <Row label="Role baru" value={`${p.seed.roles_seeded_new?.length ?? 0}`} />}
              </div>
            ) : <div className="text-[10px] text-white/40 italic">Belum di-execute.</div>}
          </BlueprintCard>
          {!isReal && (
            <>
              <BlueprintCard icon={HardDrive} title="Storage (Blueprint)" prepared={p.storage?.prepared} testid="prov-storage">
                {p.storage ? (
                  <div>
                    <Row label="Driver" value={p.storage.driver} mono />
                    <Row label="Base Path" value={p.storage.base_path} mono />
                    <Row label="Folders" value={`${Object.keys(p.storage.folders || {}).length}`} />
                  </div>
                ) : <div className="text-[10px] text-white/40 italic">Belum di-start.</div>}
              </BlueprintCard>
              <BlueprintCard icon={GitBranch} title="Migration (Blueprint)" prepared={!!p.migration?.ready} testid="prov-migration">
                {p.migration ? (
                  <div>
                    <Row label="Plan" value={`${p.migration.plan?.length || 0} migration`} />
                    <Row label="Executed" value={`${p.migration.executed_count ?? 0}`} />
                  </div>
                ) : <div className="text-[10px] text-white/40 italic">Belum di-start.</div>}
              </BlueprintCard>
            </>
          )}
          {p.configuration && (
            <BlueprintCard icon={GitBranch} title="Configuration" prepared={p.configuration.status === "ACTIVE"} testid="prov-configuration">
              <Row label="Status" value={p.configuration.status} />
              <Row label="Version" value={`v${p.configuration.config_version ?? "?"}`} />
              <Row label="Environment" value={p.configuration.environment} />
            </BlueprintCard>
          )}
          {p.environment_snapshot && (
            <BlueprintCard icon={GitBranch} title="Environment" prepared={p.environment_snapshot.status === "ACTIVE"} testid="prov-environment">
              <Row label="Status" value={p.environment_snapshot.status} />
              <Row label="Version" value={`v${p.environment_snapshot.config_version ?? "?"}`} />
              <Row label="Database" value={p.environment_snapshot.database_name} mono />
            </BlueprintCard>
          )}
        </div>
      </div>

      <div className="mt-4">
        <Link to="/platform/provisioning" className="text-xs text-white/50 hover:text-white underline">← Semua provisioning</Link>
      </div>
    </PlatformLayout>
  );
}
